/**
 * @internal true
 * @module briefing/useBriefingData
 * Hook central de dados para o Dashboard Briefing Diário.
 * Agrega múltiplas fontes: agenda, incidentes, frequência.
 * Cada bloco carrega independentemente para exibição progressiva.
 */
import { useState, useEffect, useMemo, useCallback } from 'react';
import { api } from '../../api/client';
import { useAuthStore } from '../../store/authStore';
import type { EventoConsolidado } from './briefing.types';

interface IncidenteResumo {
  id: string;
  titulo: string;
  tipoIncidente: string;
  severidade: string;
  status: string;
  descricao: string;
  criadoEm: string;
  aluno?: { id: string; nome: string };
  turma?: { nome: string };
}

interface FrequenciaResumo {
  presentesHoje: number;
  ausentesHoje: number;
  semRegistroHoje: number;
  pctPresenca: number;
  chamadasRealizadas: number;
  totalAlunos: number;
  totalTurmas: number;
  totalProfessores: number;
  inconsistencias: number;
}

export interface BriefingData {
  /* Perfil & saudação */
  nomeUsuario: string;
  nomeEscola: string;
  perfil: string;
  cargo: string;
  saudacao: string;

  /* Agenda do dia */
  eventosHoje: EventoConsolidado[];
  loadingAgenda: boolean;

  /* Alertas / Incidentes */
  incidentesAtivos: IncidenteResumo[];
  loadingIncidentes: boolean;

  /* Frequência do dia */
  frequencia: FrequenciaResumo;
  loadingFrequencia: boolean;

  /* Ações */
  resolverIncidente: (id: string) => Promise<void>;
  recarregar: () => void;
}

function getSaudacao(): string {
  const h = new Date().getHours();
  if (h < 12) return 'Bom dia';
  if (h < 18) return 'Boa tarde';
  return 'Boa noite';
}

function getDataFormatada(): string {
  return new Date().toLocaleDateString('pt-BR', {
    weekday: 'long',
    day: '2-digit',
    month: 'long',
    year: 'numeric',
  });
}

export function useBriefingData(): BriefingData & { dataFormatada: string } {
  const user = useAuthStore((s) => s.user);

  /* === Estados de dados === */
  const [eventosHoje, setEventosHoje] = useState<EventoConsolidado[]>([]);
  const [loadingAgenda, setLoadingAgenda] = useState(true);

  const [incidentesAtivos, setIncidentesAtivos] = useState<IncidenteResumo[]>([]);
  const [loadingIncidentes, setLoadingIncidentes] = useState(true);

  const [frequencia, setFrequencia] = useState<FrequenciaResumo>({
    presentesHoje: 0,
    ausentesHoje: 0,
    semRegistroHoje: 0,
    pctPresenca: 0,
    chamadasRealizadas: 0,
    totalAlunos: 0,
    totalTurmas: 0,
    totalProfessores: 0,
    inconsistencias: 0,
  });
  const [loadingFrequencia, setLoadingFrequencia] = useState(true);

  const escolaId = user?.escolaId || '';
  const perfil = user?.perfil || 'ESCOLA';
  const cargo = (user as any)?.cargo || '';

  /* === Carregar Agenda === */
  const carregarAgenda = useCallback(async () => {
    setLoadingAgenda(true);
    try {
      const hoje = new Date().toISOString().split('T')[0];
      const params = new URLSearchParams({
        inicio: `${hoje}T00:00:00Z`,
        fim: `${hoje}T23:59:59Z`,
      });
      const res = await api.get(`/agenda/consolidada?${params}`);
      const evts = (res.data || []) as EventoConsolidado[];
      // Ordenar por data
      evts.sort((a, b) => new Date(a.data).getTime() - new Date(b.data).getTime());
      setEventosHoje(evts);
    } catch {
      setEventosHoje([]);
    } finally {
      setLoadingAgenda(false);
    }
  }, []);

  /* === Carregar Incidentes === */
  const carregarIncidentes = useCallback(async () => {
    if (!escolaId) {
      setLoadingIncidentes(false);
      return;
    }
    setLoadingIncidentes(true);
    try {
      const { data } = await api.get(`/escolas/${escolaId}/incidentes`, {
        params: { status: 'ABERTO' },
      });
      const items = Array.isArray(data) ? data : (data as any)?.items || [];
      setIncidentesAtivos(items);
    } catch {
      setIncidentesAtivos([]);
    } finally {
      setLoadingIncidentes(false);
    }
  }, [escolaId]);

  /* === Carregar Frequência === */
  const carregarFrequencia = useCallback(async () => {
    if (!escolaId) {
      setLoadingFrequencia(false);
      return;
    }
    setLoadingFrequencia(true);
    try {
      const token = localStorage.getItem('ac_token');
      const base = import.meta.env.VITE_API_URL || '/api/v1';
      const res = await fetch(`${base}/admin/dashboard/hoje?escolaId=${escolaId}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (res.ok) {
        const d = await res.json();
        setFrequencia({
          presentesHoje: d.presentesHoje ?? 0,
          ausentesHoje: d.ausentesHoje ?? 0,
          semRegistroHoje: d.semRegistroHoje ?? 0,
          pctPresenca: d.pctPresenca ?? 0,
          chamadasRealizadas: d.chamadasRealizadas ?? 0,
          totalAlunos: d.totalAlunos ?? d.alunosAtivos ?? 0,
          totalTurmas: d.totalTurmas ?? d.turmasAtivas ?? 0,
          totalProfessores: d.totalProfessores ?? d.profsAtivos ?? 0,
          inconsistencias: d.inconsistencias ?? 0,
        });
      }
    } catch {
      /* silencioso */
    } finally {
      setLoadingFrequencia(false);
    }
  }, [escolaId]);

  /* === Carregar tudo em paralelo === */
  const recarregar = useCallback(() => {
    carregarAgenda();
    carregarIncidentes();
    carregarFrequencia();
  }, [carregarAgenda, carregarIncidentes, carregarFrequencia]);

  useEffect(() => {
    recarregar();
  }, [recarregar]);

  /* === Ação: Resolver incidente === */
  const resolverIncidente = useCallback(
    async (id: string) => {
      await api.patch(`/escolas/${escolaId}/incidentes/${id}/resolver`, {
        resolvidoPorId: user?.id,
        resolucao: 'Resolvido pela equipe',
      });
      // Remover do estado local (optimistic)
      setIncidentesAtivos((prev) => prev.filter((i) => i.id !== id));
    },
    [escolaId, user?.id],
  );

  /* === Derivados === */
  const saudacao = useMemo(() => getSaudacao(), []);
  const dataFormatada = useMemo(() => getDataFormatada(), []);

  const primeiroNome = useMemo(() => {
    const nome = user?.nome || '';
    return nome.split(' ')[0] || 'Usuário';
  }, [user?.nome]);

  return {
    nomeUsuario: primeiroNome,
    nomeEscola: (user as any)?.escola?.nome || (user as any)?.nomeEscola || '',
    perfil,
    cargo,
    saudacao,
    dataFormatada,
    eventosHoje,
    loadingAgenda,
    incidentesAtivos,
    loadingIncidentes,
    frequencia,
    loadingFrequencia,
    resolverIncidente,
    recarregar,
  };
}
